import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Brain, Sparkles, Calendar, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import { Toaster } from "./ui/sonner";

const ComingSoon = () => {
  const [adminCode, setAdminCode] = useState("");
  const [showAdminInput, setShowAdminInput] = useState(false);

  const handleAdminAccess = () => {
    if (adminCode === "LETOPT2025") {
      // Store admin access in localStorage
      localStorage.setItem("admin_access", "true");
      // Redirect to full site
      window.location.href = "/admin";
    } else {
      toast.error("Invalid admin code");
      setAdminCode("");
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const floatAnimation = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-page overflow-hidden">
      <Toaster />

      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center">
        <motion.div
          className="max-w-4xl mx-auto px-6 text-center"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          {/* Logo & Brand */}
          <motion.div
            className="flex items-center justify-center mt-12 mb-8"
            variants={fadeInUp}
          >
            <motion.div
              className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mr-4 shadow-2xl"
              variants={floatAnimation}
              animate="animate"
            >
              <Brain className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="heading-1 gradient-text">Let's Optimize</h1>
          </motion.div>

          {/* AI Badge */}
          <motion.div
            className="ai-feature-badge mx-auto mb-8"
            variants={fadeInUp}
          >
            <Sparkles className="w-4 h-4" />
            <span>Revolutionary AI Customer Service Platform</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h2 className="heading-hero mb-6" variants={fadeInUp}>
            The Future of Customer Service
            <br />
            <span className="gradient-text">Is Almost Here</span>
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            className="body-large mb-12 max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            We're putting the finishing touches on the most advanced AI-powered
            customer service platform. Transform your business with intelligent
            agents that handle email, SMS, chat, and voice interactions
            seamlessly.
          </motion.p>

          {/* Features Preview */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
            variants={fadeInUp}
          >
            {[
              {
                icon: Mail,
                title: "AI Email Marketing",
                desc: "Personalized campaigns",
              },
              {
                icon: Sparkles,
                title: "Smart SMS Chat",
                desc: "Context-aware conversations",
              },
              {
                icon: Brain,
                title: "Intelligent Chatbot",
                desc: "24/7 customer support",
              },
              {
                icon: Zap,
                title: "Voice Sales Agent",
                desc: "Human-like interactions",
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="ai-service-card bg-card hover-lift p-6"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="heading-3 mb-2 text-primary">{feature.title}</h3>
                <p className="body-small text-secondary">{feature.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Call to Action */}
          <motion.div className="max-w-md mx-auto mb-16" variants={fadeInUp}>
            <Card className="modern-form">
              <CardContent>
                <h3 className="heading-3 mb-6 text-primary text-center">
                  Ready to Transform Your Business?
                </h3>
                <p className="body-medium text-secondary text-center mb-6">
                  Contact our team to learn more about our AI-powered customer
                  service solutions.
                </p>
                <div className="space-y-4">
                  <a
                    href="mailto:info@letsOptimize.ai"
                    className="btn-primary w-full"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Us
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Launch Timeline */}
          <motion.div
            className="flex items-center justify-center space-x-8 mb-16"
            variants={fadeInUp}
          >
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-purple-400" />
              <span className="body-medium text-secondary">Launching Soon</span>
            </div>
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
            <div className="flex items-center space-x-3">
              <Zap className="w-5 h-5 text-blue-400" />
              <span className="body-medium text-secondary">
                Next-Gen AI Technology
              </span>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div className="text-center" variants={fadeInUp}>
            <p className="body-small text-muted mb-4">
              Questions? Get in touch with our team
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8">
              <a
                href="mailto:info@letsOptimize.ai"
                className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span className="body-small">info@letsOptimize.ai</span>
              </a>
            </div>
          </motion.div>

          {/* Admin Access (Hidden) */}
          <motion.div className="fixed bottom-4 left-4" variants={fadeInUp}>
            {!showAdminInput ? (
              <button
                onClick={() => setShowAdminInput(true)}
                className="w-8 h-8 opacity-10 hover:opacity-30 transition-opacity"
              >
                <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
              </button>
            ) : (
              <Card className="modern-form w-64">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <Input
                      type="password"
                      value={adminCode}
                      onChange={(e) => setAdminCode(e.target.value)}
                      placeholder="Admin access code"
                      className="bg-bg-page border-border-input focus:border-border-input-focus text-gray-900 text-sm"
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAdminAccess()
                      }
                    />
                    <div className="flex space-x-2">
                      <Button
                        onClick={handleAdminAccess}
                        className="btn-primary text-xs px-3 py-1 h-8"
                      >
                        Access
                      </Button>
                      <Button
                        onClick={() => {
                          setShowAdminInput(false);
                          setAdminCode("");
                        }}
                        className="btn-secondary text-xs px-3 py-1 h-8"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer
        className="relative z-10 py-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
              <Brain className="w-3 h-3 text-white" />
            </div>
            <span className="body-medium text-primary font-medium">
              Let's Optimize
            </span>
          </div>
          <p className="body-small text-muted">
            © 2025 Let's Optimize. All rights reserved.
          </p>
        </div>
      </motion.footer>
    </div>
  );
};

export default ComingSoon;
